import {
  ColumnDef,
  TableOptions,
  useReactTable,
  CellContext,
  Table,
  getExpandedRowModel,
  getGroupedRowModel,
  getPaginationRowModel,
  ExpandedState,
} from '@tanstack/react-table';
import { useVirtualizer, Virtualizer } from '@tanstack/react-virtual';
import { createContext, PropsWithChildren, RefObject, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { expandColumnConfig } from './columns/expand';
import { selectColumnConfig } from './columns/select';
import { ColumnMeta } from './table-v2';
import { useDidUpdateEffect } from '../../utils/hooks';

export interface TableMeta {
  isLoading: boolean;
}

interface TableState<TData> {
  table: ReturnType<typeof useReactTable<TData>>;
  stickyColumns?: string[];
  enableColumnOrdering?: boolean;
  totalRowCount?: number;
  isLoading?: boolean;
  rowVirtualizer?: Virtualizer<HTMLTableSectionElement, Element>;
  enableRowVirtualization: { minRows: number } | boolean;
  tableContainerRef?: RefObject<HTMLDivElement>;
}

const defaultTableState: TableState<unknown> = {
  table: {} as Table<unknown>,
  isLoading: false,
  enableRowVirtualization: { minRows: 200 },
};

/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
export const TableStateContext = createContext<TableState<any>>(defaultTableState);

interface TableStateProviderProps<TData> extends PropsWithChildren {
  options: TableOptions<TData>;
  stickyColumns?: string[];
  enableColumnOrdering?: boolean;
  totalRowCount?: number;
  isLoading?: boolean;
  enableRowVirtualization: { minRows: number } | boolean;
  estimateRowHeight?: (index: number) => number;
  onRowSelect?: (rows: TData[]) => void;
  onRowDeselect?: (rows: TData[]) => void;
  groupBy?: string;
  hideGroupByColumn?: boolean;
}

const Skeleton: <TData, TValue>({ column }: CellContext<TData, TValue>) => JSX.Element = <TData, TValue>({
  column,
}: CellContext<TData, TValue>) => {
  return (column.columnDef.meta as ColumnMeta | undefined)?.classes ? (
    <div className={`skeleton-td ${(column.columnDef.meta as ColumnMeta).classes}`} />
  ) : (
    <div className="skeleton-td w-20" />
  );
};

const estimateRowHeightDefault = () => 58.8125;

// eslint-disable-next-line max-lines-per-function
export const TableStateProvider = <T,>(props: TableStateProviderProps<T>) => {
  const {
    options: defaultOptions,
    stickyColumns: defaultStickyColumns,
    enableRowVirtualization,
    children,
    estimateRowHeight,
    isLoading,
    onRowSelect,
    onRowDeselect,
    groupBy,
    hideGroupByColumn,
    ...rest
  } = props;

  const [expanded, setExpanded] = useState<ExpandedState>({});

  useEffect(() => {
    // Expand all rows when groupBy is set
    if (groupBy) {
      setExpanded(true);
    }
  }, [groupBy]);

  // In case of loading change render method to display loading div for all columns else keep default column config
  const columns = useMemo<ColumnDef<T>[]>(() => {
    let newColumns = defaultOptions.columns;
    // add first column as checkbox column
    if (defaultOptions.enableRowSelection) {
      newColumns = [selectColumnConfig(onRowSelect, onRowDeselect, isLoading), ...newColumns];
    }
    if (defaultOptions.enableGrouping && groupBy !== undefined) {
      newColumns = [expandColumnConfig(), ...newColumns];
    }
    if (isLoading) {
      newColumns = newColumns.map((column) => ({
        ...column,
        cell: Skeleton,
      })) as ColumnDef<T>[];
    }
    return newColumns;
  }, [
    defaultOptions.columns,
    defaultOptions.enableRowSelection,
    defaultOptions.enableGrouping,
    groupBy,
    isLoading,
    onRowDeselect,
    onRowSelect,
  ]);

  // generate rows when loading else use data passed to the table
  const data = useMemo(() => {
    return isLoading ? (Array.from({ length: 30 }).fill({}) as T[]) : defaultOptions.data;
  }, [defaultOptions.data, isLoading]);

  let newStickyColumns = defaultStickyColumns;
  // by default make select column sticky
  if (defaultOptions.enableRowSelection) {
    newStickyColumns = defaultStickyColumns ? ['expand', 'select', ...defaultStickyColumns] : ['expand', 'select'];
  } else {
    newStickyColumns = defaultStickyColumns ? ['expand', ...defaultStickyColumns] : ['expand'];
  }

  const columnVisibility = useMemo(() => {
    const state = { ...defaultOptions.state?.columnVisibility };
    if (groupBy && hideGroupByColumn) {
      state[groupBy] = false;
    }
    return state;
  }, [groupBy, defaultOptions.state?.columnVisibility, hideGroupByColumn]);

  const columnPinning = useMemo(() => {
    return {
      left: [...(newStickyColumns || []), ...(defaultOptions.state?.columnPinning?.left || [])],
      right: defaultOptions.state?.columnPinning?.right || [],
    };
  }, [newStickyColumns, defaultOptions.state?.columnPinning]);

  const options: TableOptions<T> = {
    getExpandedRowModel: defaultOptions.enableGrouping ? getExpandedRowModel() : undefined,
    getGroupedRowModel: defaultOptions.enableGrouping ? getGroupedRowModel() : undefined,
    getPaginationRowModel: defaultOptions.enableGrouping ? getPaginationRowModel() : undefined,
    onExpandedChange: setExpanded,
    paginateExpandedRows: false,
    autoResetExpanded: false,
    columnResizeMode: 'onChange',
    ...defaultOptions,
    columns,
    data,
    initialState: {
      ...defaultOptions.initialState,
      grouping: groupBy ? [groupBy] : [],
      sorting: groupBy ? [{ id: groupBy, desc: false }] : defaultOptions.initialState?.sorting ?? [],
    },
    state: {
      expanded,
      ...defaultOptions.state,
      columnVisibility,
      columnPinning,
    },
    meta: {
      ...defaultOptions.meta,
      isLoading,
    },
  };
  const table = useReactTable(options);
  const tableContainerRef = useRef<HTMLTableSectionElement>(null);

  const rowVirtualizer = useVirtualizer({
    getScrollElement: () => tableContainerRef.current,
    count: data.length,
    estimateSize: estimateRowHeight ?? estimateRowHeightDefault,
    overscan: 75,
  });
  const context = useMemo(
    () => ({
      table,
      stickyColumns: newStickyColumns,
      isLoading,
      rowVirtualizer,
      enableRowVirtualization,
      tableContainerRef,
      ...rest,
    }),
    [table, newStickyColumns, isLoading, rowVirtualizer, enableRowVirtualization, rest]
  );

  // Don't reset row selection on initial render
  // only reset when groupBy changes
  useDidUpdateEffect(() => {
    table.setGrouping(groupBy ? [groupBy] : []);
    table.resetRowSelection();
    table.setSorting(groupBy ? [{ id: groupBy, desc: false }] : []);
  }, [groupBy, table]);

  return <TableStateContext.Provider value={context}>{children}</TableStateContext.Provider>;
};

export const useTable = () => {
  return useContext(TableStateContext);
};
